import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, DateRangePicker, Input, Loader, Panel } from "rsuite";
import { DateRange } from "rsuite/esm/DateRangePicker";
import { AxiosResponse } from "axios";
import { apiUrls } from "../../utils/apiUrls";
import { getData } from "../../utils/reusables";
import {
  notificationContextTypes,
  typeApiResponse,
  typeCountryDto,
} from "../../utils/typeDefs";
import { NotificationContext } from "../../core/context/notificationContextProvider";
import {
  getAnalyzerNewsByCountry,
  getLiveNewsByCountry,
  saveAnalyzerP2NewsMapping,
  saveLiveP2NewsMapping,
} from "./p2NewsCountryMapApi";

const PILLAR_TWO_NEWS_MAPPER_STORAGE_KEY = "PILLAR_TWO_NEWS_MAPPER_STATE";
const PILLAR_TWO_NEWS_MAPPER_STORAGE_VERSION = 2;

const createDefaultDateRange = (): [Date, Date] => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  const startDate = new Date(endDate);
  startDate.setMonth(startDate.getMonth() - 2);
  startDate.setHours(0, 0, 0, 0);

  return [startDate, endDate];
};

const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) {
    return "th";
  }

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatReadableDate = (date: Date) =>
  `${date.getDate()}${getOrdinalSuffix(date.getDate())} ${date.toLocaleString(
    "en-US",
    { month: "long" }
  )} ${date.getFullYear()}`;

const formatDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeDateRange = (range: [Date, Date]): [Date, Date] => {
  const [startDate, endDate] = range;
  const normalizedStartDate = new Date(startDate);
  normalizedStartDate.setHours(0, 0, 0, 0);

  const normalizedEndDate = new Date(endDate);
  normalizedEndDate.setHours(23, 59, 59, 999);

  return [normalizedStartDate, normalizedEndDate];
};

const getNewsDateTimestamp = (value: string) => {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const normalizeWhitespace = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

const normalizeNewsDateForMatch = (value: string) => {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) {
    return normalizeWhitespace(value);
  }

  const normalizedDate = new Date(timestamp);
  return normalizedDate.toISOString().slice(0, 10);
};

const normalizeLinkForMatch = (value: string) => {
  if (!value.trim()) {
    return "";
  }

  try {
    const parsedUrl = new URL(value);
    const host = parsedUrl.hostname.replace(/^www\./, "").toLowerCase();
    const path = parsedUrl.pathname.replace(/\/+$/, "").toLowerCase();
    return `${host}${path}`;
  } catch {
    return value
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/[?#].*$/, "")
      .replace(/\/+$/, "");
  }
};

type PlatformNewsMapRow = {
  id: number;
  countryId: number;
  countryName: string;
  countryCode: string;
  newsId: number;
  title: string;
  description: string;
  newsDate: string;
  link: string;
  isPillarTwoNews: boolean;
};

type PillarTwoNewsMapperRow = {
  rowKey: string;
  countryId: number;
  countryName: string;
  countryCode: string;
  title: string;
  description: string;
  newsDate: string;
  link: string;
  liveNewsId: number | null;
  analyzerNewsId: number | null;
  liveMappingId: number | null;
  analyzerMappingId: number | null;
  isLivePillarTwoNews: boolean;
  isAnalyzerPillarTwoNews: boolean;
};

type PillarTwoNewsMapperProps = {
  title: string;
};

type SortDirection = "asc" | "desc";
type SummaryFilter = "all" | "checked" | "pendingAnalyzer";
type PublishTarget = "live" | "analyzer";

type PersistedPillarTwoNewsMapperState = {
  version: number;
  countryId: number | null;
  dateRange: [string, string];
  filter: string;
  newsRows: PillarTwoNewsMapperRow[];
  sortDirection: SortDirection;
  activeSummaryFilter: SummaryFilter;
};

const getPersistedPillarTwoNewsMapperState =
  (): PersistedPillarTwoNewsMapperState | null => {
    const value = sessionStorage.getItem(PILLAR_TWO_NEWS_MAPPER_STORAGE_KEY);
    if (!value) {
      return null;
    }

    try {
      const parsedValue = JSON.parse(value) as PersistedPillarTwoNewsMapperState;
      if (parsedValue.version !== PILLAR_TWO_NEWS_MAPPER_STORAGE_VERSION) {
        sessionStorage.removeItem(PILLAR_TWO_NEWS_MAPPER_STORAGE_KEY);
        return null;
      }

      return parsedValue;
    } catch {
      sessionStorage.removeItem(PILLAR_TWO_NEWS_MAPPER_STORAGE_KEY);
      return null;
    }
  };

const buildMergedRowKey = (
  countryId: number,
  liveNewsId: number | null,
  analyzerNewsId: number | null
) => `${countryId}:${liveNewsId ?? "none"}:${analyzerNewsId ?? "none"}`;

const getRowMatchKeys = (row: PlatformNewsMapRow) => {
  const keys: string[] = [];
  const normalizedLink = normalizeLinkForMatch(row.link || "");
  const normalizedTitle = normalizeWhitespace(row.title || "");
  const normalizedDate = normalizeNewsDateForMatch(row.newsDate || "");

  if (normalizedLink) {
    keys.push(`link:${row.countryId}:${normalizedLink}`);
  }

  if (normalizedTitle && normalizedDate) {
    keys.push(`title-date:${row.countryId}:${normalizedTitle}:${normalizedDate}`);
  }

  return keys;
};

const buildMergedRow = (
  liveRow: PlatformNewsMapRow | null,
  analyzerRow: PlatformNewsMapRow | null
): PillarTwoNewsMapperRow => {
  const primaryRow = liveRow || analyzerRow;

  if (!primaryRow) {
    throw new Error("Cannot build row without live or analyzer source");
  }

  return {
    rowKey: buildMergedRowKey(
      primaryRow.countryId,
      liveRow?.newsId ?? null,
      analyzerRow?.newsId ?? null
    ),
    countryId: primaryRow.countryId,
    countryName: liveRow?.countryName || analyzerRow?.countryName || "",
    countryCode: liveRow?.countryCode || analyzerRow?.countryCode || "",
    title: liveRow?.title || analyzerRow?.title || "",
    description: liveRow?.description || analyzerRow?.description || "",
    newsDate: liveRow?.newsDate || analyzerRow?.newsDate || "",
    link: liveRow?.link || analyzerRow?.link || "",
    liveNewsId: liveRow?.newsId ?? null,
    analyzerNewsId: analyzerRow?.newsId ?? null,
    liveMappingId: liveRow?.id ?? null,
    analyzerMappingId: analyzerRow?.id ?? null,
    isLivePillarTwoNews: liveRow?.isPillarTwoNews ?? false,
    isAnalyzerPillarTwoNews: analyzerRow?.isPillarTwoNews ?? false,
  };
};

const mergePlatformRows = (
  liveRows: PlatformNewsMapRow[],
  analyzerRows: PlatformNewsMapRow[]
): PillarTwoNewsMapperRow[] => {
  const analyzerIndexesByKey = new Map<string, number[]>();
  const matchedAnalyzerIndexes = new Set<number>();

  analyzerRows.forEach((row, index) => {
    getRowMatchKeys(row).forEach((key) => {
      const currentIndexes = analyzerIndexesByKey.get(key) || [];
      currentIndexes.push(index);
      analyzerIndexesByKey.set(key, currentIndexes);
    });
  });

  const mergedRows = liveRows.map((liveRow) => {
    let matchedAnalyzerRow: PlatformNewsMapRow | null = null;

    for (const key of getRowMatchKeys(liveRow)) {
      const indexes = analyzerIndexesByKey.get(key);
      if (!indexes?.length) {
        continue;
      }

      while (indexes.length > 0 && matchedAnalyzerIndexes.has(indexes[0])) {
        indexes.shift();
      }

      const analyzerIndex = indexes.shift();
      if (analyzerIndex === undefined) {
        continue;
      }

      matchedAnalyzerIndexes.add(analyzerIndex);
      matchedAnalyzerRow = analyzerRows[analyzerIndex];
      break;
    }

    return buildMergedRow(liveRow, matchedAnalyzerRow);
  });

  analyzerRows.forEach((analyzerRow, index) => {
    if (matchedAnalyzerIndexes.has(index)) {
      return;
    }

    mergedRows.push(buildMergedRow(null, analyzerRow));
  });

  return mergedRows;
};

const hasPendingAnalyzerSync = (row: PillarTwoNewsMapperRow) =>
  row.liveNewsId !== null &&
  row.analyzerNewsId !== null &&
  row.isLivePillarTwoNews !== row.isAnalyzerPillarTwoNews;

const hasCheckedState = (row: PillarTwoNewsMapperRow) =>
  row.isLivePillarTwoNews || row.isAnalyzerPillarTwoNews;

const PillarTwoNewsMapper = ({ title }: PillarTwoNewsMapperProps) => {
  const persistedState = useMemo(getPersistedPillarTwoNewsMapperState, []);
  const initialDateRange = useMemo<[Date, Date]>(() => {
    if (!persistedState?.dateRange?.[0] || !persistedState?.dateRange?.[1]) {
      return createDefaultDateRange();
    }

    return normalizeDateRange([
      new Date(persistedState.dateRange[0]),
      new Date(persistedState.dateRange[1]),
    ]);
  }, [persistedState]);

  const [countryList, setCountryList] = useState<typeCountryDto[]>([]);
  const [countryId, setCountryId] = useState<number | null>(
    persistedState?.countryId ?? null
  );
  const [newsRows, setNewsRows] = useState<PillarTwoNewsMapperRow[]>(
    persistedState?.newsRows ?? []
  );
  const [loading, setLoading] = useState(false);
  const [savingState, setSavingState] = useState<{
    rowKey: string;
    target: PublishTarget;
  } | null>(null);
  const [filter, setFilter] = useState(persistedState?.filter ?? "");
  const [dateRange, setDateRange] = useState<[Date, Date]>(initialDateRange);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    persistedState?.sortDirection ?? "desc"
  );
  const [activeSummaryFilter, setActiveSummaryFilter] = useState<SummaryFilter>(
    persistedState?.activeSummaryFilter ?? "all"
  );

  const { triggerNotification } = useContext(
    NotificationContext
  ) as notificationContextTypes;

  const selectedCountryName = useMemo(
    () => countryList.find((country) => country.id === countryId)?.name,
    [countryId, countryList]
  );

  const sortedCountryList = useMemo(
    () =>
      [...countryList].sort((left, right) => left.name.localeCompare(right.name)),
    [countryList]
  );

  const checkedCount = useMemo(
    () => newsRows.filter((row) => hasCheckedState(row)).length,
    [newsRows]
  );

  const pendingAnalyzerCount = useMemo(
    () => newsRows.filter((row) => hasPendingAnalyzerSync(row)).length,
    [newsRows]
  );

  const displayedNewsRows = useMemo(() => {
    const filteredRows = newsRows.filter((row) => {
      if (activeSummaryFilter === "checked") {
        return hasCheckedState(row);
      }

      if (activeSummaryFilter === "pendingAnalyzer") {
        return hasPendingAnalyzerSync(row);
      }

      return true;
    });

    return [...filteredRows].sort((left, right) => {
      const leftTime = getNewsDateTimestamp(left.newsDate);
      const rightTime = getNewsDateTimestamp(right.newsDate);

      return sortDirection === "asc" ? leftTime - rightTime : rightTime - leftTime;
    });
  }, [activeSummaryFilter, newsRows, sortDirection]);

  const selectedDateRangeLabel = useMemo(
    () => `${formatReadableDate(dateRange[0])} to ${formatReadableDate(dateRange[1])}`,
    [dateRange]
  );

  const dateRangePickerValue = useMemo<DateRange>(
    () => [new Date(dateRange[0]), new Date(dateRange[1])],
    [dateRange]
  );

  useEffect(() => {
    getData(apiUrls.country.base).then(
      (result: AxiosResponse<{ data: typeCountryDto[] } & typeApiResponse>) => {
        setCountryList(result.data.data || []);
      }
    );
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      PILLAR_TWO_NEWS_MAPPER_STORAGE_KEY,
      JSON.stringify({
        version: PILLAR_TWO_NEWS_MAPPER_STORAGE_VERSION,
        countryId,
        dateRange: [dateRange[0].toISOString(), dateRange[1].toISOString()],
        filter,
        newsRows,
        sortDirection,
        activeSummaryFilter,
      } satisfies PersistedPillarTwoNewsMapperState)
    );
  }, [activeSummaryFilter, countryId, dateRange, filter, newsRows, sortDirection]);

  const fetchNews = async (
    nextCountryId = countryId,
    nextDateRange = dateRange,
    nextFilter = filter
  ) => {
    if (!nextCountryId) {
      setNewsRows([]);
      return;
    }

    setLoading(true);

    try {
      const startDate = nextDateRange[0].toISOString();
      const endDate = nextDateRange[1].toISOString();

      const [liveResponse, analyzerResponse] = await Promise.all([
        getLiveNewsByCountry(
          nextCountryId,
          startDate,
          endDate,
          nextFilter.trim()
        ),
        getAnalyzerNewsByCountry(
          nextCountryId,
          startDate,
          endDate,
          nextFilter.trim()
        ),
      ]);

      const liveSucceeded =
        liveResponse?.status === 200 && liveResponse?.data?.succeeded;
      const analyzerSucceeded =
        analyzerResponse?.status === 200 && analyzerResponse?.data?.succeeded;

      if (!liveSucceeded) {
        triggerNotification(
          false,
          liveResponse?.data?.message || "Failed to load live news mappings"
        );
        setNewsRows([]);
        return;
      }

      if (!analyzerSucceeded) {
        triggerNotification(
          false,
          analyzerResponse?.data?.message ||
            "Analyzer mappings could not be loaded. Showing live results only."
        );
      }

      setNewsRows(
        mergePlatformRows(
          liveResponse.data.data || [],
          analyzerSucceeded ? analyzerResponse.data.data || [] : []
        )
      );
    } catch (error: any) {
      triggerNotification(false, error.message || "Failed to load news mappings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  const handleToggle = async (
    row: PillarTwoNewsMapperRow,
    target: PublishTarget,
    checked: boolean
  ) => {
    const newsId = target === "live" ? row.liveNewsId : row.analyzerNewsId;

    if (!newsId) {
      triggerNotification(
        false,
        `${target === "live" ? "Live" : "Analyzer"} news is not available for this row`
      );
      return;
    }

    setSavingState({ rowKey: row.rowKey, target });

    try {
      const requestPayload = {
        countryId: row.countryId,
        newsId,
        isPillarTwoNews: checked,
      };

      const response =
        target === "analyzer"
          ? await saveAnalyzerP2NewsMapping(requestPayload)
          : await saveLiveP2NewsMapping(requestPayload);

      if (response.status === 200 && response.data.succeeded) {
        setNewsRows((rows) =>
          rows.map((item) => {
            if (item.rowKey !== row.rowKey) {
              return item;
            }

            if (target === "analyzer") {
              return {
                ...item,
                analyzerMappingId:
                  response.data.data?.id || item.analyzerMappingId,
                isAnalyzerPillarTwoNews: checked,
              };
            }

            return {
              ...item,
              liveMappingId: response.data.data?.id || item.liveMappingId,
              isLivePillarTwoNews: checked,
            };
          })
        );

        triggerNotification(
          true,
          target === "analyzer"
            ? response.data.message || "Analyzer mapping updated"
            : response.data.message || "Live mapping updated"
        );
      } else {
        triggerNotification(
          false,
          response.data?.message ||
            `Failed to update ${target === "analyzer" ? "Analyzer" : "Live"} mapping`
        );
      }
    } catch (error: any) {
      triggerNotification(
        false,
        error.message ||
          `Failed to update ${target === "analyzer" ? "Analyzer" : "Live"} mapping`
      );
    } finally {
      setSavingState(null);
    }
  };

  const resetFilters = () => {
    const defaultDateRange = createDefaultDateRange();
    setFilter("");
    setDateRange(defaultDateRange);
    setSortDirection("desc");
    setActiveSummaryFilter("all");

    if (countryId) {
      fetchNews(countryId, defaultDateRange, "");
    }
  };

  const renderSummaryChip = (
    filterKey: SummaryFilter,
    label: string,
    modifier?: string
  ) => (
    <button
      type="button"
      className={`pillar-two-news-mapper__stat-chip pillar-two-news-mapper__stat-chip--button ${
        modifier ? modifier : ""
      } ${
        activeSummaryFilter === filterKey
          ? "pillar-two-news-mapper__stat-chip--selected"
          : ""
      }`}
      onClick={() => setActiveSummaryFilter(filterKey)}
    >
      {label}
    </button>
  );

  return (
    <section className="content-box-title pillar-two-news-mapper">
      <h1 className="page-title pb-3">
        {title}{" "}
        {selectedCountryName && <span className="text-info">{selectedCountryName}</span>}
      </h1>

      <Panel bordered className="pillar-two-news-mapper__panel">
        <div className="pillar-two-news-mapper__toolbar">
          <div className="pillar-two-news-mapper__field-wrap">
            <label className="pillar-two-news-mapper__field-label" htmlFor="pillar-two-country">
              Country
            </label>
            <select
              id="pillar-two-country"
              className="form-control pillar-two-news-mapper__field"
              value={countryId ?? ""}
              onChange={(event) =>
                setCountryId(event.target.value ? Number(event.target.value) : null)
              }
            >
              <option value="">Select Country</option>
              {sortedCountryList.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="pillar-two-news-mapper__field-wrap pillar-two-news-mapper__field-wrap--range">
            <label className="pillar-two-news-mapper__field-label" htmlFor="pillar-two-date-range">
              Date Range
            </label>
            <DateRangePicker
              id="pillar-two-date-range"
              className="pillar-two-news-mapper__date-range-picker"
              showHeader={false}
              cleanable={false}
              placement="autoVerticalEnd"
              value={dateRangePickerValue}
              onChange={(value) => {
                if (value?.[0] && value?.[1]) {
                  setDateRange(normalizeDateRange([value[0], value[1]]));
                }
              }}
              renderValue={([startDate, endDate]) =>
                `${formatDateInputValue(startDate)} to ${formatDateInputValue(endDate)}`
              }
              format="yyyy-MM-dd"
              character=" to "
              placeholder="Select date range"
            />
          </div>

          <div className="pillar-two-news-mapper__field-wrap">
            <label className="pillar-two-news-mapper__field-label" htmlFor="pillar-two-filter">
              Keyword
            </label>
            <Input
              id="pillar-two-filter"
              value={filter}
              onChange={setFilter}
              placeholder="Search title or description"
              className="pillar-two-news-mapper__field-input"
            />
          </div>

          <div className="pillar-two-news-mapper__action-group">
            <Button
              appearance="primary"
              onClick={() => fetchNews()}
              disabled={!countryId}
            >
              Search
            </Button>
            <Button appearance="ghost" onClick={resetFilters} disabled={!countryId}>
              Reset
            </Button>
          </div>
        </div>

        <div className="pillar-two-news-mapper__summary">
          <div className="pillar-two-news-mapper__summary-copy">
            <span className="pillar-two-news-mapper__summary-label">Range:</span>
            <span>{selectedDateRangeLabel}</span>
          </div>
          <div className="pillar-two-news-mapper__summary-stats">
            {renderSummaryChip("all", `Total rows: ${newsRows.length}`)}
            {renderSummaryChip(
              "checked",
              `Checked: ${checkedCount}`,
              "pillar-two-news-mapper__stat-chip--accent"
            )}
            {renderSummaryChip(
              "pendingAnalyzer",
              `Pending Analyzer publish: ${pendingAnalyzerCount}`,
              "pillar-two-news-mapper__stat-chip--warning"
            )}
          </div>
        </div>
      </Panel>

      <Panel bordered className="pillar-two-news-mapper__panel">
        {loading ? (
          <div className="pillar-two-news-mapper__loading-state">
            <Loader size="md" content="Loading Pillar Two news..." vertical />
          </div>
        ) : displayedNewsRows.length === 0 ? (
          <div className="pillar-two-news-mapper__empty-state">
            <h4>No news items found</h4>
            <p>
              Try adjusting the country, date range, keyword, or summary chip
              filters.
            </p>
          </div>
        ) : (
          <div className="pillar-two-news-mapper__table-wrap">
            <table className="pillar-two-news-mapper__table">
              <colgroup>
                <col style={{ width: "210px" }} />
                <col style={{ width: "170px" }} />
                <col style={{ width: "32%" }} />
                <col style={{ width: "48%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="pillar-two-news-mapper__th pillar-two-news-mapper__th--pillar">
                    Pillar Two
                  </th>
                  <th className="pillar-two-news-mapper__th">
                    <div className="pillar-two-news-mapper__th-inner">
                      <span>Date</span>
                      <button
                        type="button"
                        className="pillar-two-news-mapper__sort-toggle"
                        aria-label={
                          sortDirection === "asc"
                            ? "Sort date newest first"
                            : "Sort date oldest first"
                        }
                        title={
                          sortDirection === "asc"
                            ? "Sort date newest first"
                            : "Sort date oldest first"
                        }
                        onClick={() =>
                          setSortDirection((current) =>
                            current === "asc" ? "desc" : "asc"
                          )
                        }
                      >
                        <span
                          className={`pillar-two-news-mapper__sort-arrow ${
                            sortDirection === "asc"
                              ? "pillar-two-news-mapper__sort-arrow--active"
                              : ""
                          }`}
                        >
                          ^
                        </span>
                        <span
                          className={`pillar-two-news-mapper__sort-arrow ${
                            sortDirection === "desc"
                              ? "pillar-two-news-mapper__sort-arrow--active"
                              : ""
                          }`}
                        >
                          v
                        </span>
                      </button>
                    </div>
                  </th>
                  <th className="pillar-two-news-mapper__th">Title</th>
                  <th className="pillar-two-news-mapper__th">Description</th>
                </tr>
              </thead>
              <tbody>
                {displayedNewsRows.map((row) => {
                  const isPendingAnalyzer = hasPendingAnalyzerSync(row);
                  const isSavingLive =
                    savingState?.rowKey === row.rowKey &&
                    savingState.target === "live";
                  const isSavingAnalyzer =
                    savingState?.rowKey === row.rowKey &&
                    savingState.target === "analyzer";

                  return (
                    <tr key={row.rowKey} className="pillar-two-news-mapper__row">
                      <td className="pillar-two-news-mapper__td pillar-two-news-mapper__td--pillar">
                        <div className="pillar-two-news-mapper__target-stack">
                          <div className="pillar-two-news-mapper__target-line pillar-two-news-mapper__target-line--live">
                            <Checkbox
                              checked={row.isLivePillarTwoNews}
                              disabled={!row.liveNewsId || Boolean(isSavingLive)}
                              onChange={(_, checked) =>
                                handleToggle(row, "live", Boolean(checked))
                              }
                            >
                              <span className="pillar-two-news-mapper__target-label">
                                Live
                              </span>
                            </Checkbox>
                            {isSavingLive ? (
                              <Loader size="xs" />
                            ) : (
                              <span className="pillar-two-news-mapper__status-text">
                                <span
                                  className={`pillar-two-news-mapper__status-dot ${
                                    row.isLivePillarTwoNews
                                      ? "pillar-two-news-mapper__status-dot--active"
                                      : ""
                                  }`}
                                />
                                {row.liveNewsId
                                  ? row.isLivePillarTwoNews
                                    ? "Included"
                                    : "Excluded"
                                  : "Unavailable"}
                              </span>
                            )}
                          </div>

                          <div className="pillar-two-news-mapper__target-line pillar-two-news-mapper__target-line--analyzer">
                            <Checkbox
                              checked={row.isAnalyzerPillarTwoNews}
                              disabled={!row.analyzerNewsId || Boolean(isSavingAnalyzer)}
                              onChange={(_, checked) =>
                                handleToggle(row, "analyzer", Boolean(checked))
                              }
                            >
                              <span className="pillar-two-news-mapper__target-label">
                                Analyzer
                              </span>
                            </Checkbox>
                            {isSavingAnalyzer ? (
                              <Loader size="xs" />
                            ) : (
                              <span className="pillar-two-news-mapper__status-text">
                                <span
                                  className={`pillar-two-news-mapper__status-dot ${
                                    row.isAnalyzerPillarTwoNews
                                      ? "pillar-two-news-mapper__status-dot--active"
                                      : ""
                                  }`}
                                />
                                {row.analyzerNewsId
                                  ? row.isAnalyzerPillarTwoNews
                                    ? "Included"
                                    : "Excluded"
                                  : "Unavailable"}
                              </span>
                            )}
                          </div>

                          {isPendingAnalyzer && (
                            <span className="pillar-two-news-mapper__analyzer-status pillar-two-news-mapper__analyzer-status--pending">
                              <span className="pillar-two-news-mapper__analyzer-status-dot" />
                              Pending Analyzer publish
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="pillar-two-news-mapper__td pillar-two-news-mapper__td--date">
                        {row.newsDate}
                      </td>
                      <td className="pillar-two-news-mapper__td">
                        {row.link ? (
                          <a
                            className="pillar-two-news-mapper__news-link"
                            href={row.link}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {row.title}
                          </a>
                        ) : (
                          row.title
                        )}
                      </td>
                      <td className="pillar-two-news-mapper__td">
                        {row.description?.length > 280
                          ? `${row.description.slice(0, 280)} ...`
                          : row.description}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </section>
  );
};

export default PillarTwoNewsMapper;
